

















/**
 * FUPOF-193 — a row of links that scroll to the sections of a long
 * configurator page.
 *
 * Deliberately NOT sticky. The design review settled this: the page already
 * spends a lot of vertical space on the headline, the stock toggle and the
 * fixed Add-to-Quote bar, and pinning a second rail would leave little room
 * for the content it navigates. These are a one-time "jump down to the part I
 * care about" affordance, not a persistent nav — "you just jump in there, and
 * from then on you can collapse and expand the cards, you can scroll."
 *
 * Because of that, `activeId` tracks the last link used rather than whatever
 * is currently on screen: a scroll-spy would be spending effort on an
 * indicator the user can no longer see. The first section is active initially,
 * matching the mockup.
 *
 * Styles live here rather than in tailwind.css's @layer components. The rule
 * that shared styles belong there exists because `.tab-btn` was copy-pasted
 * into two views; a component is a stronger answer to the same problem, since
 * there is only one definition to change.
 */
import { Component, Prop, Vue } from "vue-property-decorator";

export interface JumpLinkSection {
  /** The `id` of the element to scroll to. */
  id: string;
  label: string;
}

@Component
export default class JumpLinks extends Vue {
  @Prop({ type: Array, default: () => [] }) sections!: JumpLinkSection[];
  @Prop({ type: String, default: "Page sections" }) ariaLabel!: string;

  protected activeId: string | null = null;

  created() {
    this.activeId = this.sections.length ? this.sections[0].id : null;
  }

  /**
   * Announce the jump before scrolling, so a parent can open a collapsed
   * section — a link that scrolls to a card the user then has to expand
   * themselves has only done half its job. The scroll is deferred a tick so it
   * measures the section at its opened height.
   *
   * Public so a page can jump on the rep's behalf — the umbrella page lands
   * on its Base card when a setup's base or cover is opened from the cart —
   * through the same expand-then-scroll path and header offset a click takes.
   */
  public goTo(id: string): void {
    this.activeId = id;
    this.$emit("jump", id);
    this.$nextTick(() => this.scrollTo(id));
  }

  private scrollTo(id: string): void {
    const target = document.getElementById(id);
    if (!target) return;
    const top =
      target.getBoundingClientRect().top + window.pageYOffset - this.headerOffset;
    window.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
  }

  /**
   * The sticky stack's height, measured rather than hard-coded: App.vue nests
   * the impersonation and edit-mode banners inside the same sticky wrapper as
   * the header, so it is ~85px normally and taller while impersonating or
   * editing a quote. A fixed offset under-shoots in exactly those states and
   * leaves the heading hidden behind the bar.
   */
  private get headerOffset(): number {
    const sticky = document.querySelector("#app > .sticky") as HTMLElement | null;
    return (sticky?.offsetHeight ?? 0) + 12;
  }
}
